import React, { useState, useMemo } from 'react';
import {
  Button,
  Card,
  Typography,
  Alert,
  Space,
  Form,
  Checkbox,
  Modal,
  Steps,
  Spin,
  Dropdown,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DownOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { usePpiAnalysis } from './hooks/usePpiAnalysis';
import { getReportRunCount } from '../../services/api';
import { useUser } from '../../context/UserContext';
import {
  VehicleOverviewPage,
  MechanicalConditionPage,
  ValueEligibilityPage,
} from './components/ReportPages';
import { NextSteps } from './components';
import { AnalysisStep, AnalysisStepsState, VehicleFormData } from '../../types';

const { Title, Text } = Typography;
const { Step } = Steps;

const reportPages = [
  {
    key: 'email_entry',
    name: 'Create Report',
    steps: [],
  },
  {
    key: 'vehicle_overview',
    name: 'Vehicle Overview',
    steps: ['flags_and_highlights'],
  },
  {
    key: 'mechanical_condition',
    name: 'Condition Report',
    steps: ['common_issues', 'maintenance', 'image_analysis', 'suggested_repairs'],
  },
  {
    key: 'value_eligibility',
    name: 'Value & Eligibility',
    steps: ['cpo_eligibility', 'market_insights'],
  },
  {
    key: 'next_steps',
    name: 'Next Steps',
    steps: ['market_insights'],
  },
];

const getPageStatus = (page: any, analysisSteps: AnalysisStepsState, analysisStarted: boolean) => {
  if (page.steps.length === 0) {
    return 'completed';
  }

  const pageSteps = page.steps.map((key: string) => analysisSteps[key]);

  if (pageSteps.some((step: AnalysisStep) => step?.status === 'error')) {
    return 'error';
  }
  if (pageSteps.every((step: AnalysisStep) => step?.status === 'completed' || step?.status === 'skipped')) {
    return 'completed';
  }
  if (pageSteps.some((step: AnalysisStep) => step?.status === 'in_progress')) {
    return 'in_progress';
  }
  if (analysisStarted && pageSteps.some((step: AnalysisStep) => step?.status === 'pending')) {
    return 'in_progress';
  }

  return 'pending';
};

interface CreatePpiReportProps {
  docId: string;
  onComplete?: () => void;
  onBack?: () => void;
  vehicleDetails?: Partial<VehicleFormData>;
}


const CreatePpiReport: React.FC<CreatePpiReportProps> = ({ docId, onComplete, onBack, vehicleDetails }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sendEmail, setSendEmail] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1500);
  const { email } = useUser();

  const {
    loading,
    analysisStarted,
    analysisSteps,
    isComplete,
    startAnalysis,
    sentEmailAddress,
    emailError,
    resetState,
    similarCars,
  } = usePpiAnalysis();

  const wasAnalyzing = React.useRef(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1500);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle deep linking via URL hash
  // Handle deep linking via URL hash
  React.useEffect(() => {
    const handleHashChange = () => {
      // In a hash-router, the "path" and "query" are inside the hash. A "sub-hash" is also possible.
      // e.g., #/path?query=1#sub-hash
      const subHash = window.location.hash.split('#').pop() || '';
      if (subHash) {
        const pageIndex = reportPages.findIndex(p => p.key === subHash);
        if (pageIndex !== -1) {
          setCurrentPage(prev => {
            if (prev !== pageIndex) return pageIndex;
            return prev;
          });
        }
      }
    };

    // Check initial hash
    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []); // Run once on mount

  React.useEffect(() => {
    const pageKey = reportPages[currentPage]?.key;
    if (pageKey) {
      const hashParts = window.location.hash.split('#');
      // The base part of the hash is the router path and query (e.g., #/create-report?docId=...)
      const baseHash = hashParts.slice(0, 2).join('#');

      const newHash = `${baseHash}#${pageKey}`;

      if (window.location.hash !== newHash) {
        // Use replaceState to update hash without adding to history stack
        // The final argument must be the full new URL path
        window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
      }
    }
  }, [currentPage]);


  // Handle Start Modal
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (analysisStarted && !isComplete) {
      wasAnalyzing.current = true;
      timer = setTimeout(() => {
        Modal.info({
          title: 'Report Generation Started',
          icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
          content: (
            <div>
              <p>Your report has started! Please do not navigate away from this page or close the tab.</p>
              <p>The report will take approximately 2-3 minutes to complete.</p>
            </div>
          ),
          onOk() { },
        });
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [analysisStarted, isComplete]);

  // Handle Completion Modal
  React.useEffect(() => {
    if (isComplete && wasAnalyzing.current) {
      wasAnalyzing.current = false;
      Modal.success({
        title: 'Report Complete',
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        content: (
          <div>
            <p>Your report is complete! Please review your results and download the PDF on the Next Steps page.</p>
          </div>
        ),
        onOk() { },
      });
    }
  }, [isComplete]);

  const pageStatuses = useMemo(() => reportPages.map(page => getPageStatus(page, analysisSteps, analysisStarted)), [analysisSteps, analysisStarted]);

  const handleNext = () => {
    if (currentPage < reportPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleStepClick = (index: number) => {
    const page = reportPages[index];
    const status = pageStatuses[index];

    if (page.key === 'email_entry' || (status !== 'pending' && status !== 'in_progress') || (page.key === 'next_steps' && isComplete)) {
      setCurrentPage(index);
    }
  };

  const handleStartAnalysis = () => {
    startAnalysis(sendEmail);
    handleNext();
  };

  const handleRerunAnalysis = async () => {
    try {
      const { number_of_times_run: runCount } = await getReportRunCount(docId);

      let confirmationMessage = 'This will rerun the analysis with the latest information. Only do this if you have added new details like images or a vehicle history report.';

      if (runCount >= 1) {
        confirmationMessage += ' You have already run this report once for free. You will be charged credits for this rerun.';
      }

      Modal.confirm({
        title: 'Rerun Analysis?',
        icon: <ExclamationCircleOutlined />,
        content: confirmationMessage,
        okText: 'Confirm Rerun',
        onOk: () => {
          resetState();
          handleStartAnalysis();
        },
      });
    } catch (error) {
      console.error("Failed to get report run count", error);
      // Fallback to original behavior if API fails
      Modal.confirm({
        title: 'Rerun Analysis?',
        icon: <ExclamationCircleOutlined />,
        content: 'This will rerun the analysis with the latest information. Only do this if you have added new details like images or a vehicle history report.',
        okText: 'Confirm Rerun',
        onOk: () => {
          resetState();
          handleStartAnalysis();
        },
      });
    }
  };

  const renderCurrentPage = () => {
    const page = reportPages[currentPage];
    const status = pageStatuses[currentPage];

    if (analysisStarted && status === 'in_progress' && page.key !== 'email_entry') {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <LoadingOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4}>Analysis in progress...</Title>
          <Text type="secondary">Compiling {page.name}</Text>
        </div>
      );
    }

    switch (page.key) {
      case 'email_entry':
        return (
          <Card>
            <Title level={4}>Your Comprehensive Report is Ready to Generate</Title>
            <Text type="secondary">
              Our AI will now analyze the vehicle details, images, and history report to generate a your complete Pacer Scout Analysis.
            </Text>
            <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
              This Includes:
            </Text>
            <Space direction="vertical" style={{ marginBottom: 16, marginLeft: 16 }}>
              <Text>Highlights & Red Flags</Text>
              <Text>Common issues for this model</Text>
              <Text>Maintenance history & CPO eligibility</Text>
              <Text>Image & damage analysis</Text>
              <Text>Market insights & a buying guide with next steps</Text>
            </Space>
            <Form layout="vertical" style={{ marginTop: 24 }}>
              {email && (
                <Form.Item>
                  <Checkbox
                    checked={sendEmail}
                    onChange={(e: CheckboxChangeEvent) => setSendEmail(e.target.checked)}
                    disabled={loading}
                  >
                    Send a copy of the report to {email}
                  </Checkbox>
                </Form.Item>
              )}
            </Form>
          </Card>
        );
      case 'vehicle_overview':
        return <VehicleOverviewPage results={analysisSteps} vehicleDetails={vehicleDetails} />;
      case 'mechanical_condition':
        return <MechanicalConditionPage results={analysisSteps} />;
      case 'value_eligibility':
        return <ValueEligibilityPage results={analysisSteps} similarCars={similarCars} />;
      case 'next_steps':
        return (
          <NextSteps
            docId={docId}
            onBack={handleBack}
            email={sentEmailAddress}
            emailError={emailError}
            sendEmail={sendEmail}
            onComplete={handleNext}
          />
        );
      default:
        return null;
    }
  };

  const renderRightButtons = () => {
    const currentStepKey = reportPages[currentPage].key;
    const currentStatus = pageStatuses[currentPage];

    if (currentStepKey === 'email_entry') {
      if (analysisStarted && !isComplete) {
        return (
          <Button type="primary" onClick={handleNext} icon={<RightOutlined />}>
            Continue to Analysis
          </Button>
        );
      }
      if (isComplete) {
        return (
          <Space>
            <Button onClick={handleRerunAnalysis} danger>
              Rerun Analysis
            </Button>
            <Button type="primary" onClick={handleNext} icon={<RightOutlined />}>
              View Previous
            </Button>
          </Space>
        );
      }
      return (
        <Button type="primary" onClick={handleStartAnalysis} loading={loading}>
          Start Analysis
        </Button>
      );
    }

    if (currentStepKey === 'value_eligibility') {
      return (
        <Button
          type="primary"
          onClick={handleNext}
          icon={<RightOutlined />}
          disabled={currentStatus !== 'completed'}
        >
          Finish & View Report
        </Button>
      );
    }

    if (currentStepKey !== 'next_steps' && currentStepKey !== 'email_entry') {
      return (
        <Button
          type="primary"
          onClick={handleNext}
          icon={<RightOutlined />}
          disabled={currentStatus !== 'completed'}
        >
          Continue
        </Button>
      );
    }

    return null;
  };

  const stepsContent = (
    <Steps direction="vertical" current={currentPage} size="small">
      {reportPages.map((page, index) => {
        const status = pageStatuses[index];
        let icon;
        switch (status) {
          case 'in_progress':
            icon = <Spin size="small" />;
            break;
          case 'completed':
            icon = <CheckCircleOutlined />;
            break;
          case 'error':
            icon = <CloseCircleOutlined />;
            break;
          default:
            if (page.key === 'next_steps' && isComplete) {
              icon = <QuestionCircleOutlined />;
            } else {
              icon = undefined;
            }
        }

        const isClickable = status === 'completed' || status === 'error' || (page.key === 'next_steps' && isComplete) || page.key === 'email_entry';

        return (
          <Step
            key={page.key}
            title={page.name}
            icon={icon}
            disabled={!isClickable}
            onClick={() => handleStepClick(index)}
            style={{ cursor: !isClickable ? 'not-allowed' : 'pointer' }}
          />
        );
      })}
    </Steps>
  );

  return (
    <div style={{ position: 'relative' }}>
      {!isMobile ? (
        <div style={{ position: 'absolute', left: '-270px', top: 0, width: '240px' }}>
          <Card>{stepsContent}</Card>
        </div>
      ) : null}
      <div>
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Title level={3} style={{ textAlign: 'center', margin: 0 }}>
            {reportPages[currentPage].name}
          </Title>
          {isMobile && (
            <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
              <Dropdown overlay={<Card>{stepsContent}</Card>}>
                <Button type="text">
                  <MenuOutlined /><DownOutlined />
                </Button>
              </Dropdown>
            </div>
          )}
        </div>
        <Card bodyStyle={{ padding: 0 }}>{renderCurrentPage()}</Card>
        {analysisStarted && !isComplete && (
          <Alert
            message="IMPORTANT: Keep this page open while we finish your inspection. Closing or refreshing may interrupt your report. It may take a few minutes."
            type="warning"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
        <Space
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginTop: 24,
          }}
        >
          <Button
            onClick={currentPage === 0 ? onBack : handleBack}
            icon={<LeftOutlined />}
            disabled={currentPage === 0 && (!onBack || (analysisStarted && !isComplete))}
          >
            Back
          </Button>
          {renderRightButtons()}
        </Space>
      </div>


    </div>
  );
};

export default CreatePpiReport;